import { useEffect, useMemo, useState } from 'react';

/**
 * Activity Tracker — when and where safety events happen.
 *
 * A read-only analytics panel over the events we already store: peak hours,
 * busiest cameras, the kind mix, and — when a camera is picked — a hotspot
 * heatmap built from each event's detection boxes, drawn over that camera's
 * current frame. Parameterised by apiBase so it serves both the staff view and
 * a tenant's own portal against their scoped, RLS-filtered data.
 */

type Win = '24h' | '7d' | '30d';

interface Activity {
  window: Win;
  cameraId: number | null;
  total: number;
  critical: number;
  open: number;
  peakHour: number | null;
  byHour: number[];
  byCamera: Array<{ id: number | null; camera: string; count: number }>;
  byKind: Array<{ kind: string; count: number }>;
  byDay: Array<{ day: string; count: number }>;
  hotspots: number[][] | null;
  hotspotMax: number;
  gridCols: number;
  gridRows: number;
}

const KIND_LABEL: Record<string, string> = {
  ppe_violation: 'PPE violation',
  fire: 'Smoke / fire',
  intrusion: 'Zone intrusion',
  occupancy: 'Occupancy',
  near_miss: 'Near-miss',
  person: 'Person',
};
const hourLabel = (h: number) => `${String(h).padStart(2, '0')}:00`;

export function SafetyActivityPanel({
  apiBase,
  cameras,
}: {
  apiBase: string;
  cameras: Array<{ id: number; name: string }>;
}) {
  const [win, setWin] = useState<Win>('7d');
  const [cameraId, setCameraId] = useState<number | ''>('');
  const [a, setA] = useState<Activity | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(Date.now());

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams({ window: win });
    if (cameraId) qs.set('cameraId', String(cameraId));
    fetch(`${apiBase}/activity?${qs.toString()}`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setA)
      .catch(() => setA(null))
      .finally(() => setLoading(false));
    setTick(Date.now());
  }, [apiBase, win, cameraId]);

  const maxHour = useMemo(() => Math.max(1, ...(a?.byHour ?? [0])), [a]);
  const maxCam = useMemo(() => Math.max(1, ...(a?.byCamera ?? []).map((c) => c.count)), [a]);

  return (
    <div className="panel" style={{ marginTop: 20 }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <strong>Activity — when &amp; where</strong>
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          <select value={cameraId} onChange={(e) => setCameraId(e.target.value ? Number(e.target.value) : '')} style={{ fontSize: 13 }}>
            <option value="">All cameras</option>
            {cameras.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <div className="row" style={{ gap: 0 }}>
            {(['24h', '7d', '30d'] as Win[]).map((w, i) => (
              <button
                key={w}
                onClick={() => setWin(w)}
                className="btn sm"
                style={{ borderRadius: i === 0 ? '6px 0 0 6px' : i === 2 ? '0 6px 6px 0' : 0, background: win === w ? 'var(--accent)' : undefined, color: win === w ? '#fff' : undefined }}
              >
                {w}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="muted" style={{ marginTop: 10 }}>Loading…</div>
      ) : !a || a.total === 0 ? (
        <div className="muted" style={{ marginTop: 10 }}>No safety events in this period.</div>
      ) : (
        <>
          <div className="row" style={{ gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
            <Stat label="Events" value={String(a.total)} />
            <Stat label="Critical" value={String(a.critical)} tone="var(--bad)" />
            <Stat label="Open" value={String(a.open)} tone="var(--warn)" />
            <Stat label="Peak hour" value={a.peakHour != null ? hourLabel(a.peakHour) : '—'} tone="var(--accent)" />
          </div>

          {/* Hotspot heatmap — only meaningful for one camera's frame */}
          {a.cameraId && a.hotspots && (
            <div style={{ marginTop: 16 }}>
              <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>Hotspots — where events cluster on this camera</div>
              <div style={{ position: 'relative', width: '100%', maxWidth: 560, aspectRatio: `${a.gridCols} / ${a.gridRows}`, background: '#000', borderRadius: 8, overflow: 'hidden' }}>
                <img
                  src={`${apiBase}/cameras/${a.cameraId}/snapshot?ts=${tick}`}
                  alt="camera"
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.55 }}
                  onError={(e) => ((e.currentTarget as HTMLImageElement).style.visibility = 'hidden')}
                />
                <div style={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: `repeat(${a.gridCols}, 1fr)`, gridTemplateRows: `repeat(${a.gridRows}, 1fr)` }}>
                  {a.hotspots.flatMap((rowArr, r) =>
                    rowArr.map((n, c) => (
                      <div key={`${r}-${c}`} style={{ background: n > 0 ? `rgba(59,130,246,${0.15 + 0.75 * (n / a.hotspotMax)})` : 'transparent' }} />
                    )),
                  )}
                </div>
              </div>
            </div>
          )}

          {/* By hour of day */}
          <div style={{ marginTop: 18 }}>
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>Events by hour of day</div>
            <div className="row" style={{ gap: 2, alignItems: 'flex-end', height: 90 }}>
              {a.byHour.map((n, h) => (
                <div key={h} title={`${hourLabel(h)} — ${n}`} style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%' }}>
                  <div style={{ height: `${(n / maxHour) * 100}%`, minHeight: n > 0 ? 2 : 0, background: h === a.peakHour ? 'var(--accent)' : 'var(--border)', borderRadius: '2px 2px 0 0' }} />
                </div>
              ))}
            </div>
            <div className="row" style={{ justifyContent: 'space-between', marginTop: 2 }}>
              {[0, 6, 12, 18, 23].map((h) => <span key={h} className="muted" style={{ fontSize: 10 }}>{hourLabel(h)}</span>)}
            </div>
          </div>

          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 20, marginTop: 18 }}>
            <div>
              <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>Busiest cameras</div>
              <div className="grid" style={{ gap: 6 }}>
                {a.byCamera.map((c) => (
                  <div key={`${c.id}-${c.camera}`} className="row" style={{ gap: 8, alignItems: 'center' }}>
                    <span style={{ flex: '0 0 130px', fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.camera}</span>
                    <span style={{ flex: '0 0 44px', fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>{c.count}</span>
                    <span style={{ flex: 1, height: 8, background: 'var(--border)', borderRadius: 4, overflow: 'hidden' }}>
                      <span style={{ display: 'block', height: '100%', width: `${(c.count / maxCam) * 100}%`, background: 'var(--accent)', borderRadius: 4 }} />
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>By type</div>
              <div className="grid" style={{ gap: 6 }}>
                {a.byKind.map((k) => (
                  <div key={k.kind} className="row" style={{ justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{KIND_LABEL[k.kind] ?? k.kind}</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>{k.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="stat-tile" style={{ flex: '1 1 120px', ['--tone' as string]: tone ?? 'var(--text-dim)' }}>
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}
